import { Component, ReactNode, useState } from "react";
import { GeoJSON } from "react-leaflet";
import { createLayerComponent } from "@react-leaflet/core";
import "leaflet-kml";
import * as L from "leaflet";
import {
  useImportedLayers,
  ImportedLayer,
} from "../../context/ImportedLayersContext";
import { bindDelayedHoverTooltip } from "../GeoJSONData/tooltipDelay";

const SafeLeafletKml: any = createLayerComponent(
  (props: any, context: any) => {
    try {
      const KmlClass = (L as any).KML;
      const instance = new KmlClass(props.kml, props.kmlOptions);
      if (context.map.options.preferCanvas) {
        setTimeout(
          (map: any) => {
            map._renderer?._update();
          },
          0,
          context.map
        );
      }
      return { instance, context };
    } catch (error) {
      console.error("Erro ao criar camada KML nativa:", error);
      throw error;
    }
  },
  (instance: any) => {
    if (instance._map?.options.preferCanvas) {
      instance._map._renderer?._update();
    }
  }
);

const IMPORTED_STYLE: L.PathOptions = {
  color: "#2E7D32",
  weight: 2,
  opacity: 0.8,
  fillOpacity: 0.2,
};

function formatImportedLayerTooltip(feature: any): string {
  if (!feature?.properties || typeof feature.properties !== "object") {
    return "";
  }

  const entries = Object.entries(feature.properties)
    .filter(
      ([, value]) => value !== undefined && value !== null && value !== ""
    )
    .map(([key, value]) => {
      const formattedValue =
        typeof value === "object" ? JSON.stringify(value) : String(value);
      return `
        <div style="margin: 2px 0;">
          <strong>${key}</strong>: ${formattedValue}
        </div>
      `;
    })
    .join("");

  if (!entries) {
    return "";
  }

  return `<div style="max-width: 220px; word-break: break-word;">${entries}</div>`;
}

class KmlErrorBoundary extends Component<
  { onError: () => void; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { onError: () => void; children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    this.props.onError();
    console.warn("Erro ao renderizar KML nativo:", error);
  }

  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

function ImportedLayerRenderer({ layer }: { layer: ImportedLayer }) {
  const { registerLeafletLayer, unregisterLeafletLayer } = useImportedLayers();
  const [kmlRenderFailed, setKmlRenderFailed] = useState(false);

  if (!layer.isVisible) return null;

  const canRenderNativeKml =
    layer.format === "kml" &&
    layer.rawKml &&
    layer.kmlSafeForPlugin !== false &&
    !kmlRenderFailed;

  if (canRenderNativeKml) {
    return (
      <KmlErrorBoundary
        onError={() => {
          setKmlRenderFailed(true);
          console.warn(
            `Falha ao renderizar KML nativo para camada ${layer.id}, usando GeoJSON como fallback.`
          );
        }}
      >
        <SafeLeafletKml
          kml={layer.rawKml as XMLDocument}
          ref={(ref: any) => {
            if (ref) registerLeafletLayer(layer.id, ref);
            else unregisterLeafletLayer(layer.id);
          }}
        />
      </KmlErrorBoundary>
    );
  }

  if (
    layer.format === "kml" &&
    layer.rawKml &&
    layer.kmlSafeForPlugin === false
  ) {
    console.warn(
      `KML nativo ignorado para camada ${layer.id} porque contém elementos que não são seguros para o plugin KML. Usando GeoJSON.`
    );
  }

  if (!layer.geojson) {
    return null;
  }

  if (
    !layer.geojson.features ||
    !Array.isArray(layer.geojson.features) ||
    layer.geojson.features.length === 0
  ) {
    console.warn(
      `Layer ${layer.id} não tem geometrias válidas, ignorando renderização.`
    );
    return null;
  }

  const onEachFeature = (feature: any, layerFeature: L.Layer) => {
    const tooltipContent = formatImportedLayerTooltip(feature);
    if (tooltipContent) {
      const isLineLayer =
        layerFeature instanceof L.Polyline &&
        !(layerFeature instanceof L.Polygon);
      const getOpenLatLng = (e: L.LeafletMouseEvent) => {
        if (isLineLayer) {
          return e.latlng;
        }

        return (layerFeature as any).getLatLng?.() ?? e.latlng;
      };

      bindDelayedHoverTooltip({
        layer: layerFeature,
        content: tooltipContent,
        tooltipOptions: {
          permanent: false,
          direction: "top",
          className: "custom-tooltip",
          offset: [0, -10],
          opacity: 1,
          sticky: isLineLayer,
        },
        isLineLayer,
        getOpenLatLng,
      });
    }
  };

  return (
    <GeoJSON
      data={layer.geojson}
      style={IMPORTED_STYLE}
      onEachFeature={onEachFeature}
      ref={(ref) => {
        if (ref) registerLeafletLayer(layer.id, ref);
        else unregisterLeafletLayer(layer.id);
      }}
    />
  );
}

const RenderImportedLayers = () => {
  const { importedLayers } = useImportedLayers();

  return (
    <>
      {importedLayers.map((layer) => (
        <ImportedLayerRenderer key={layer.id} layer={layer} />
      ))}
    </>
  );
};

export default RenderImportedLayers;
